import {Component, DestroyRef, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {NavigationService} from '../../../Services/navigation.service';
import {Project} from '../../../DataModels/project.model';

@Component({
  selector: 'app-project-form',
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.css'],
  standalone: false
})
export class ProjectFormComponent implements OnInit {
  vm: any = {
    title: 'Project',
    item: {} as Project,
    company: [],
    users: [],
    entities: [],
    inventoryItems: [],
    Preferences: [],
    permissions: [],
    buttonsDisabled: false,
    mainListPage: '/projects/project-list',
  };

  submitted = false;
  ProjectForm: UntypedFormGroup;

  buttonConfig: any = {
    save: true,
    saveClose: true,
    saveNew: false,
    delete: true,
    reset: true,
    close: true,
  };

  readonly statusOptions = ['Draft', 'Active', 'On Hold', 'Completed', 'Cancelled'];
  readonly billingTypeOptions = [
    {value: 'TimeAndMaterials', label: 'Time & Materials'},
    {value: 'PercentageComplete', label: 'Percentage Complete'},
    {value: 'ByTaskPercentageComplete', label: 'By Task % Complete'},
    {value: 'FixedPrice', label: 'Fixed Price'},
    {value: 'NonBillable', label: 'Non-Billable'},
  ];

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private nav: NavigationService,
    private fb: UntypedFormBuilder,
    private destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.vm.permissions = JSON.parse(this.localStorage.StorageGet('Permissions'));

    this.ProjectForm = this.fb.group({
      item_name: new UntypedFormControl('', Validators.required),
      item_description: new UntypedFormControl(''),
      item_entity_id: new UntypedFormControl(''),
      item_status: new UntypedFormControl('Draft', Validators.required),
      item_billing_type: new UntypedFormControl('TimeAndMaterials', Validators.required),
      item_start_date: new UntypedFormControl(''),
      item_due_date: new UntypedFormControl(''),
      item_budget_hours: new UntypedFormControl(0),
      item_budget_amount: new UntypedFormControl(0),
      item_percent_complete: new UntypedFormControl(0),
      item_manager_user_id: new UntypedFormControl(''),
      item_notes: new UntypedFormControl(''),
      item_default_labor_item_id: new UntypedFormControl(null),
      item_default_material_item_id: new UntypedFormControl(null),
    });

    this.loadCompanies();
    this.loadLookups();

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params['id']) {
        this.loadItem(params['id']);
      } else {
        this.vm.item = {
          company_id: this.vm.Preferences?.defaults?.company_id ?? this.vm.company?.[0]?.id ?? '',
          status: 'Draft',
          billing_type: 'TimeAndMaterials',
          budget_hours: 0,
          budget_amount: 0,
          percent_complete: 0,
        } as Project;
        this.titleService.setTitle('New Project');
      }
    });
  }

  get f() {
    return this.ProjectForm.controls;
  }

  loadCompanies() {
    this.tableDataService.getData('company?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.company = result.Data || [];
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadLookups() {
    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.vm.users = result.Data ?? result.items ?? [];
      },
      error: (err) => {
        console.error(err);
      },
    });
    this.tableDataService.getApiListData('entity', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.vm.entities = result.Data ?? result.items ?? [];
      },
      error: (err) => {
        console.error(err);
      },
    });
    this.tableDataService.getData('item', '?pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.inventoryItems = result.Data ?? result.data ?? (Array.isArray(result) ? result : []);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadItem(id: string) {
    this.tableDataService.getData('project', id).subscribe({
      next: (result: Project) => {
        this.vm.item = result;
        this.titleService.setTitle('Edit Project — ' + result.name);
        this.ProjectForm.patchValue({
          item_name: result.name,
          item_description: result.description ?? '',
          item_entity_id: result.entity_id ?? '',
          item_status: result.status,
          item_billing_type: result.billing_type,
          item_start_date: result.start_date ?? '',
          item_due_date: result.due_date ?? '',
          item_budget_hours: result.budget_hours,
          item_budget_amount: result.budget_amount,
          item_percent_complete: result.percent_complete,
          item_manager_user_id: result.manager_user_id ?? '',
          item_notes: result.notes ?? '',
          item_default_labor_item_id: result.default_labor_item_id ?? null,
          item_default_material_item_id: result.default_material_item_id ?? null,
        });
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading project');
      },
    });
  }

  buildPayload(): Project {
    const {actual_hours, tasks, milestones, ...itemBase} = (this.vm.item ?? {}) as any;
    return {
      ...itemBase,
      company_id: this.vm.item?.company_id ?? this.vm.Preferences?.defaults?.company_id ?? '',
      name: this.f['item_name'].value,
      description: this.f['item_description'].value,
      entity_id: this.f['item_entity_id'].value || null,
      status: this.f['item_status'].value,
      billing_type: this.f['item_billing_type'].value,
      start_date: this.f['item_start_date'].value || null,
      due_date: this.f['item_due_date'].value || null,
      budget_hours: +this.f['item_budget_hours'].value,
      budget_amount: +this.f['item_budget_amount'].value,
      percent_complete: +this.f['item_percent_complete'].value,
      manager_user_id: this.f['item_manager_user_id'].value || null,
      notes: this.f['item_notes'].value,
      default_labor_item_id: this.f['item_default_labor_item_id'].value || null,
      default_material_item_id: this.f['item_default_material_item_id'].value || null,
    };
  }

  onSave(closeAfter: boolean) {
    this.submitted = true;
    if (this.ProjectForm.invalid) {
      return;
    }
    this.vm.buttonsDisabled = true;
    const payload = this.buildPayload();

    if (!this.vm.item?.id) {
      this.tableDataService.post('project', payload).subscribe({
        next: (result: any) => {
          this.notification.showSuccessToast('Project saved');
          this.vm.item.id = result.id;
          this.vm.item.number = result.number;
          this.submitted = false;
          if (closeAfter) {
            this.onClose();
          }
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    } else {
      this.tableDataService.put('project', this.vm.item.id, payload).subscribe({
        next: () => {
          this.notification.showSuccessToast('Project updated');
          this.submitted = false;
          if (closeAfter) {
            this.onClose();
          }
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    }
  }

  saveClose() {
    this.onSave(true);
  }

  deleteItem() {
    if (!this.vm.item?.id) {
      return;
    }
    if (!confirm('Delete this project?')) {
      return;
    }
    this.vm.buttonsDisabled = true;
    this.tableDataService.deleteDataById('project', this.vm.item.id).subscribe({
      next: () => {
        this.notification.showSuccessToast('Project deleted');
        this.onClose();
      },
      error: (err) => {
        console.error(err);
        this.vm.buttonsDisabled = false;
      },
    });
  }

  resetForm() {
    this.submitted = false;
    if (this.vm.item?.id) {
      this.loadItem(this.vm.item.id);
    } else {
      this.ProjectForm.reset({
        item_status: 'Draft',
        item_billing_type: 'TimeAndMaterials',
        item_budget_hours: 0,
        item_budget_amount: 0,
        item_percent_complete: 0
      });
    }
  }

  onClose() {
    this.nav.backOr(this.vm.mainListPage);
  }
}
